export const customSolutionsPillar = {
  slug: "custom-solutions",
  title: "Custom Solutions | Tier-1.io",
  metaDescription:
    "Tier-1.io custom solutions for small businesses and lean teams: local IT and desktop support, managed email hosting on Google Workspace, web development, and custom AI engagements.",
  heroKicker: "Custom Solutions",
  heroTitle: "Custom solutions beyond off-the-shelf software",
  heroDescription:
    "Alongside AI automation, Tier-1.io handles the practical technology work small businesses and lean teams still need done right — local IT support, managed email, web development, and bespoke builds — with an experienced operator on the other end.",
  intro:
    "Not every problem is an AI problem. Sometimes you just need someone local to fix the computers, professional email on your own domain, a website that actually works, or a custom build scoped to your operation. These are the hands-on services we take on for clients, partners, and the occasional friend-of-the-business who needs a reliable technical partner.",
};

export const customSolutions = [
  {
    slug: "it-support",
    title: "Traditional IT Support",
    metaDescription:
      "Local, hands-on IT support for small businesses in Cherry Valley, Beaumont, and the Inland Empire: email, desktop and laptop support, printers, networking, and everyday tech help.",
    kicker: "Local, hands-on IT",
    category: "IT Support",
    heroTitle: "Traditional IT support for Inland Empire small businesses",
    heroDescription:
      "Hands-on help with the everyday technology your business runs on — email, computers, printers, Wi-Fi, and the problems that stop work — for small businesses and home offices in Cherry Valley, Beaumont, and the greater Inland Empire.",
    bestFor: [
      "Small businesses in Cherry Valley, Beaumont, and the Inland Empire with no in-house IT",
      "Solo operators and home offices that need a local person who can actually show up",
      "Owners tired of gambling on whoever answers a big-box tech support line",
    ],
    outcomes: [
      "Get computers, email, and printers working and keep them that way",
      "A local point of contact who knows your setup instead of starting over each time",
      "Faster fixes with on-site help when remote support is not enough",
      "Simple, honest guidance on what to repair, replace, or leave alone",
    ],
    process: [
      {
        title: "Assess your setup",
        description:
          "We review the computers, email, network, and devices your business depends on and flag what is fragile, slow, or a security risk.",
      },
      {
        title: "Fix and set up",
        description:
          "We resolve the immediate issues, set up new devices and accounts, and clean up the recurring problems that keep interrupting work.",
      },
      {
        title: "Support and maintain",
        description:
          "We stay available for remote and on-site help, updates, and the occasional emergency so you have someone to call when something breaks.",
      },
    ],
    deliverables: [
      "Email, desktop, and laptop setup and troubleshooting",
      "Printer, Wi-Fi, and basic network support",
      "New device setup and data migration",
      "Remote support with on-site visits in the service area",
      "Plain-English recommendations and follow-up",
    ],
    faqs: [
      {
        question: "What areas do you cover?",
        answer:
          "Traditional IT support is a local service for Cherry Valley, Beaumont, and the surrounding Inland Empire. On-site visits are limited to that area; email hosting and web development are available anywhere.",
      },
      {
        question: "Do you support both Windows and Mac?",
        answer:
          "Yes. We help with Windows and Mac computers, common email clients, printers, Wi-Fi, and the everyday devices small businesses and home offices rely on.",
      },
      {
        question: "Is this remote or on-site?",
        answer:
          "Both. Many issues are handled remotely for speed, and we come on-site within the service area when a problem needs hands on the hardware.",
      },
    ],
    cta: "Get local IT support",
  },
  {
    slug: "email-hosting",
    title: "Managed Email Hosting",
    national: true,
    metaDescription:
      "Managed email hosting on Google Workspace for small businesses. Bring your own domain and we handle setup, DNS, security, mailboxes, and ongoing support.",
    kicker: "Bring your own domain",
    category: "Email",
    heroTitle: "Professional email on your own domain, managed for you",
    heroDescription:
      "We set up and manage Google Workspace email on your own domain — mailboxes, DNS, deliverability, and security — so you get reliable, professional email without becoming your own IT department.",
    bestFor: [
      "Solo operators and small shops that want name@theirdomain instead of a generic address",
      "Owners who already have a domain but do not want to manage email settings and DNS themselves",
      "Teams that need a real person to call when email breaks, changes, or needs a new mailbox",
    ],
    outcomes: [
      "Send and receive from professional addresses on your own domain",
      "Reliable deliverability with SPF, DKIM, and DMARC configured correctly",
      "Familiar Gmail, Calendar, and Drive tools your team already knows",
      "A single point of contact for setup, changes, and troubleshooting",
    ],
    process: [
      {
        title: "Domain and account setup",
        description:
          "We create your Google Workspace organization, verify your domain, and plan the mailboxes, aliases, and groups you need.",
      },
      {
        title: "DNS and deliverability",
        description:
          "We configure MX records, SPF, DKIM, and DMARC so your mail routes correctly and lands in inboxes instead of spam folders.",
      },
      {
        title: "Migration and handoff",
        description:
          "We move existing mail if needed, set up each user, and show your team how everything works day to day.",
      },
      {
        title: "Ongoing management",
        description:
          "We stay on as your admin for new mailboxes, security settings, and the occasional what-happened-to-my-email question.",
      },
    ],
    deliverables: [
      "Configured Google Workspace tenant on your domain",
      "Mailboxes, aliases, and shared groups",
      "SPF, DKIM, and DMARC records",
      "Optional mailbox migration",
      "Ongoing admin and support",
    ],
    faqs: [
      {
        question: "Do I need to buy a domain first?",
        answer:
          "You bring your own domain. If you do not have one yet, we can help you register the right one before we set up email on it.",
      },
      {
        question: "Is this Gmail?",
        answer:
          "It is Google Workspace, which gives you Gmail, Calendar, Drive, and Meet on your own domain with business-grade controls and support.",
      },
      {
        question: "Who does this service fit?",
        answer:
          "It is best for small shops, solo operators, and lean teams that want professional email handled by someone they trust rather than doing admin work themselves.",
      },
    ],
    cta: "Set up managed email",
  },
  {
    slug: "web-development",
    title: "Web Development",
    national: true,
    metaDescription:
      "Web development for small businesses and lean teams: fast, maintainable marketing sites, landing pages, and custom web apps built by an experienced engineer.",
    kicker: "Sites and web apps",
    category: "Web",
    heroTitle: "Web development for businesses that need more than a template",
    heroDescription:
      "We design and build fast, maintainable websites and web applications — from a clean marketing site to a custom app connected to your data — built by an engineer who has shipped production systems for over 20 years.",
    bestFor: [
      "Businesses that have outgrown a drag-and-drop builder or an aging site",
      "Owners who need a landing page, marketing site, or booking flow that actually converts",
      "Teams that need a custom web app or internal tool instead of forcing their process into generic software",
    ],
    outcomes: [
      "A fast, responsive site built on modern, maintainable code",
      "SEO-ready structure, metadata, and performance out of the box",
      "Custom features and integrations wired into the tools you already use",
      "A clear handoff so you are not locked to a single vendor",
    ],
    process: [
      {
        title: "Scope and design",
        description:
          "We define the pages, features, content, and integrations you need, then agree on a simple design and structure before building.",
      },
      {
        title: "Build and integrate",
        description:
          "We develop the site or app with clean code, connect forms, payments, scheduling, or APIs, and set up hosting and analytics.",
      },
      {
        title: "Test and launch",
        description:
          "We check performance, accessibility, mobile layout, and SEO basics, then deploy and verify everything works in production.",
      },
      {
        title: "Support and iterate",
        description:
          "We can stay on for updates, new pages, and improvements, or hand off documentation so your team can run it.",
      },
    ],
    deliverables: [
      "Designed and built website or web application",
      "Responsive, accessible, SEO-ready pages",
      "Form, payment, scheduling, or API integrations",
      "Hosting, deployment, and analytics setup",
      "Documentation and optional ongoing support",
    ],
    faqs: [
      {
        question: "Do you only build marketing sites?",
        answer:
          "No. We build everything from simple marketing sites and landing pages to custom web applications and internal tools connected to your data.",
      },
      {
        question: "What do you build with?",
        answer:
          "We choose the right tools for the job, typically modern React and serverless infrastructure, favoring boring, maintainable technology over trends.",
      },
      {
        question: "Can you take over an existing site?",
        answer:
          "Often, yes. We can audit what you have, fix or rebuild it, and take over hosting and maintenance depending on how it was built.",
      },
      {
        question: "Should I just use Shopify or Squarespace instead?",
        answer:
          "Sometimes yes. If your products fit a standard catalog and you are fine renting the platform, an off-the-shelf store is the fastest path. A custom build pays off when you have specific workflows, unusual inventory, integrations, or plans to scale — see the comparison above for the trade-offs and real costs.",
      },
    ],
    comparison: {
      title: "Custom build vs. Shopify, Squarespace, and payment links",
      description:
        "Off-the-shelf platforms are the fastest way to start selling, but you rent them forever and build your business inside their limits. A custom build costs more upfront and pays off when you outgrow the template. The honest way to compare is a fixed platform fee plus processing fees on whatever you actually sell — no inflated estimates.",
      columns: [
        { name: "Custom build (Tier-1.io)" },
        { name: "Shopify / BigCommerce", href: "https://www.shopify.com/pricing" },
        { name: "Squarespace / Wix", href: "https://www.squarespace.com/pricing" },
        { name: "PayPal / Stripe links", href: "https://stripe.com/pricing" },
      ],
      rows: [
        {
          label: "Best for",
          values: [
            "Specific workflows, unusual products, integrations, or plans to scale",
            "Standard product catalogs that fit the platform's model",
            "Simple stores and content sites with a handful of products",
            "Selling one or a few items without a real storefront",
          ],
        },
        {
          label: "Upfront cost",
          values: [
            { text: "One-time build, typically $6k–$25k+", tone: "mid" },
            { text: "Low — theme and setup", tone: "good" },
            { text: "Low — template setup", tone: "good" },
            { text: "Minimal", tone: "good" },
          ],
        },
        {
          label: "Monthly platform fee",
          values: [
            { text: "$0 platform fee — you pay only hosting (~$5–30/mo)", tone: "good" },
            { text: "From $29/mo (Shopify, billed yearly) or $39/mo monthly, up to $399/mo; BigCommerce from $29/mo", tone: "mid" },
            { text: "From ~$16–36/mo depending on plan", tone: "mid" },
            { text: "$0/mo", tone: "good" },
          ],
        },
        {
          label: "Processing fee on each sale",
          values: [
            { text: "~2.9% + $0.30 (Stripe or your processor)", tone: "neutral" },
            { text: "~2.9% + $0.30; +0.5–2% surcharge unless you use Shopify Payments", tone: "mid" },
            { text: "~2.9% + $0.30; Squarespace Business plan adds 3% on top", tone: "mid" },
            { text: "~2.9% + $0.30 (PayPal or Stripe)", tone: "neutral" },
          ],
        },
        {
          label: "What you actually pay*",
          values: [
            { text: "One-time build + hosting + processing on what you sell", tone: "neutral" },
            { text: "Monthly plan + processing on what you sell + any paid apps", tone: "neutral" },
            { text: "Monthly plan + processing on what you sell", tone: "neutral" },
            { text: "Processing on what you sell — no fixed monthly cost", tone: "neutral" },
          ],
        },
        {
          label: "Inventory tracking",
          values: [
            { text: "Yes — built to your rules: bundles, kits, multi-location", tone: "good" },
            { text: "Yes — solid built-in inventory", tone: "good" },
            { text: "Basic built-in inventory", tone: "mid" },
            { text: "No real inventory system", tone: "bad" },
          ],
        },
        {
          label: "Design & customization",
          values: [
            { text: "Unlimited — anything you can design", tone: "good" },
            { text: "Themes and apps; limited outside the platform", tone: "mid" },
            { text: "Templates; limited flexibility", tone: "mid" },
            { text: "Buttons on a page you build elsewhere", tone: "bad" },
          ],
        },
        {
          label: "You own the code & data",
          values: [
            { text: "Yes — full ownership, fully portable", tone: "good" },
            { text: "No — you rent the platform", tone: "bad" },
            { text: "No — you rent the platform", tone: "bad" },
            { text: "Partial — the processor owns the checkout", tone: "mid" },
          ],
        },
        {
          label: "SEO & performance control",
          values: [
            { text: "Full control over speed, structure, and metadata", tone: "good" },
            { text: "Good, within platform limits", tone: "mid" },
            { text: "Limited; template-bound", tone: "mid" },
            { text: "Depends on the page you host it on", tone: "mid" },
          ],
        },
        {
          label: "Vendor lock-in",
          values: [
            { text: "None — move hosts or developers anytime", tone: "good" },
            { text: "High — migrating off is costly", tone: "bad" },
            { text: "High — content is tied to the platform", tone: "bad" },
            { text: "Low for links, but there is no store to move", tone: "mid" },
          ],
        },
        {
          label: "Strengths",
          values: [
            "Fits your exact process, integrates with your tools, no platform tax, scales with you",
            "Fast to launch, large app ecosystem, handles the commerce basics well",
            "Cheapest and quickest for simple catalogs, all-in-one",
            "Zero setup, no monthly fee, fine for a single product",
          ],
        },
        {
          label: "Trade-offs",
          values: [
            "Higher upfront cost and lead time; needs a developer to build it right",
            "Monthly and app costs stack up; you are boxed into their model and possible surcharges",
            "Limited customization and inventory; many stores outgrow it",
            "No cart, catalog, or real inventory; not a true storefront",
          ],
        },
      ],
      footnote:
        "*Platform fees are fixed and shown above; processing fees of about 2.9% + $0.30 per sale apply on every option and scale with what you actually sell — so your total depends on your own volume, not an estimate. Prices as of 2026; see each provider's linked pricing page for current rates.",
    },
    cta: "Start a web project",
  },
];

export default customSolutions;
